
import {
  computed,
  defineComponent, isVNode,
  nextTick,
  onBeforeMount, onMounted, onUnmounted,
  provide,
  reactive,
  ref,
  Ref,
  toRefs,
  useSlots,
  watch
} from 'vue'
import NavigationTabBarItem from '@/layouts/components/NavigationTabBarItem.vue'

export default defineComponent({
  name: 'NavigationTabBar',
  props: {
    modelValue: {
      type: [String, Number, Symbol],
      required: false,
    },
    defaultFirstTab: {
      type: Boolean,
      default: true,
    }
  },
  emits: ['update:modelValue'],
  setup (props, context) {

    let _defaultFirstTab = Boolean(props.defaultFirstTab)

    const state = reactive<{
      underline: null | HTMLDivElement,
      innerEl: Ref<HTMLDivElement | undefined>,
      resizeObserver: null | ResizeObserver,
      activeEl: Ref<HTMLElement | undefined>,
      left: string,
      width: string,
      initialiseTabAsCurrent: typeof props.modelValue
    }>({
      underline: null,
      innerEl: ref(),
      resizeObserver: null,
      activeEl: ref(),
      left: '',
      width: '',
      initialiseTabAsCurrent: props.modelValue
    })

    const setPositionUnderline = () => {
      if (state.activeEl && state.underline) {
        state.left = `${state.activeEl.offsetLeft}px`
        state.width = `${state.activeEl.clientWidth}px`
      }
    }

    const setCurrentTab = (value?: typeof props.modelValue, el?: HTMLElement) => {
      state.activeEl = el
      if (state.initialiseTabAsCurrent) {
        state.initialiseTabAsCurrent = undefined
      }
      if (props.modelValue === value || value === undefined) return
      context.emit('update:modelValue', value)
    }

    watch(() => props.modelValue, () => {
      setPositionUnderline()
    })

    const slots = useSlots()
    const defaultSlot = computed(() => slots?.default ? slots.default() : undefined)

    const activateFirstTab = (defaultSlotValue) => {
      if (_defaultFirstTab && !props.modelValue && defaultSlotValue?.length) {
        let tabItems = defaultSlotValue
        if (
          typeof defaultSlotValue[0].type === 'symbol' &&
          defaultSlotValue[0].type.toString() === Symbol('Fragment').toString() &&
          Array.isArray(defaultSlotValue[0].children)
        ) {
          tabItems = defaultSlotValue[0].children.filter(isVNode)
        }
        const firstItem = tabItems.find(vnode => vnode.type === NavigationTabBarItem)
        if (firstItem && firstItem.props?.name) {
          _defaultFirstTab = false
          unwatchDefaultSlot()
          state.initialiseTabAsCurrent = firstItem.props.name
        }
      }
    }

    onMounted(() => {
      activateFirstTab(defaultSlot.value)
      if (state.innerEl) {
        state.resizeObserver = new ResizeObserver(() => void nextTick().then(void nextTick().then(setPositionUnderline)))
        state.resizeObserver.observe(state.innerEl)
      }
    })

    onUnmounted(() => state.resizeObserver?.disconnect())

    const unwatchDefaultSlot = watch(defaultSlot, activateFirstTab)


    const initialiseTabAsCurrent = computed(() => state.initialiseTabAsCurrent)
    const currentTab = computed(() => props.modelValue)

    provide('currentTab', {
      initialiseTabAsCurrent,
      currentTab,
      setCurrentTab
    })

    return {
      ...toRefs(state),
      currentTab,
    }
  }
})
